import { Injectable } from '@angular/core';
import { StoreService } from 'projects/dop-core/src/public-api';
import { SalesCatalogueType } from 'projects/dop-ordering/src/lib/models/sales-catalogue-type.enum';
import { Analysis, AnalysisToolTip, AnalysisType, CiType, QuotedItem } from 'projects/dop-ordering/src/public-api';
import { StoreKey } from 'src/app/core/store/ngrx-store.keys';
import { CommercialItem } from 'src/app/shared/models/commercial-item.model';
import { ApplicabilityEnum } from 'src/app/shared/models/legacy-matrix-applicability/applicability.enum';
import { ProductApplicabilityResponseModel } from 'src/app/shared/models/legacy-matrix-applicability/product-applicability-response.model';
import { MemberType } from 'src/app/shared/models/sales-catalogue.model';
import { Sample } from 'src/app/shared/models/sample.model';
import { AnalysesToolTipService } from '../services/analyses-tool-tip.service';

@Injectable({
  providedIn: 'root'
})
export class SalesCatalogueService {

  constructor(
    private analysesToolTipService: AnalysesToolTipService,
    private storeService: StoreService) { }

  mapAnalysisToCommercialItem(analysis: Analysis): CommercialItem {
    const commercialItem = new CommercialItem();
    switch (analysis.analysisType) {
      case AnalysisType.SalesCatalogueTest:
        commercialItem.code = analysis.testCode;
        commercialItem.name = analysis.displayName;
        commercialItem.type = analysis.analysisType;
        commercialItem.price = analysis.price;
        commercialItem.invoiceableItemParentCode = analysis.salesCatalogCode;
        commercialItem.preparationTests = analysis.preparationTestCodes;
        commercialItem.isMandatory = analysis.isMandatory;
        commercialItem.comment = analysis.structuredComment;
        commercialItem.executionNumber = analysis.executionNumber;
        commercialItem.key = analysis.salesCatalogCode + '|' + analysis.testCode + '|' + SalesCatalogueType.Test;
        if (analysis.testBundleCode) {
          commercialItem.testBundleCode = analysis.testBundleCode;
          commercialItem.testBundleDisplayName = analysis.testBundleDisplayName;
          commercialItem.key = analysis.salesCatalogCode + '|' + analysis.testBundleCode + '|'
            + SalesCatalogueType.TestBundle + '|' + analysis.testCode;
        }
        break;
      case AnalysisType.SalesCataloguePackage:
        commercialItem.code = analysis.packageCode;
        commercialItem.name = analysis.displayName,
          commercialItem.type = analysis.analysisType,
          commercialItem.price = analysis.price,
          commercialItem.invoiceableItemParentCode = analysis.salesCatalogCode,
          commercialItem.preparationTests = analysis.preparationTestCodes,
          commercialItem.isMandatory = analysis.isMandatory;
        commercialItem.comment = analysis.structuredComment;
        commercialItem.key = analysis.salesCatalogCode + '|' + analysis.packageCode + '|' + SalesCatalogueType.Package;
        commercialItem.executionNumber = analysis.executionNumber;
        break;
      case AnalysisType.SalesCatalogueTestBundle:
        commercialItem.code = analysis.testCode;
        commercialItem.name = analysis.displayName;
        commercialItem.type = analysis.analysisType;
        commercialItem.price = analysis.price;
        commercialItem.comment = analysis.structuredComment;
        commercialItem.invoiceableItemParentCode = analysis.salesCatalogCode;
        commercialItem.preparationTests = analysis.preparationTestCodes;
        commercialItem.isMandatory = analysis.isMandatory;
        commercialItem.key = analysis.salesCatalogCode + '|' + analysis.testCode + '|' + SalesCatalogueType.Test;
        commercialItem.testBundleCode = analysis.testBundleCode;
        commercialItem.testBundleDisplayName = analysis.testBundleDisplayName;
        commercialItem.key = analysis.salesCatalogCode + '|' + analysis.testBundleCode + '|'
          + SalesCatalogueType.TestBundle + '|' + analysis.testCode;
        commercialItem.executionNumber = analysis.executionNumber;
        break;
    }
    return commercialItem;
  }

  mapAnalysisArrayToCommercialItemArray(alreadySelectedSCAnalysis: Analysis[]): CommercialItem[] {
    const commercialItems: CommercialItem[] = [];
    for (const analysis of alreadySelectedSCAnalysis) {
      let commercialItem = this.mapAnalysisToCommercialItem(analysis);
      if (commercialItem.code)
        commercialItems.push(commercialItem);
    }
    return commercialItems;
  }

  mapSelectedAnalysis(node): CommercialItem[] {
    const items: CommercialItem[] = [];
    const item = new CommercialItem();
    item.code = node.data.code;
    item.name = node.data.name;
    item.key = node.key;
    item.invoiceableItemParentCode = node.parentCode;
    item.price = node.data.priceValue;
    item.type = this.getAnalysisType(node.data.type);
    item.preparationTests = node.data.preparationTests;
    item.comment = node.data.comment;
    item.executionNumber = node.data.executionNumber !== undefined ? node.data.executionNumber : 1 ;
    switch (item.type) {
      case AnalysisType.SalesCatalogueTest:
        if (node.parent) { // test inside test bundle;
          item.invoiceableItemParentCode = node.parent.parentCode;
          item.testBundleCode = node.parentCode;
          item.testBundleDisplayName = node.parent.data.name;
        }
        items.push(item);
        break;
      case AnalysisType.SalesCataloguePackage:
        items.push(item);
        break;
      case AnalysisType.SalesCatalogueTestBundle:
        for (const test of node.children) {
          const testBundleTest: CommercialItem = {
            code: test.data.code,
            key: test.key,
            name: test.data.name,
            comment: test.data.comment,
            invoiceableItemParentCode: node.parentCode,
            testBundleCode: test.parentCode,
            testBundleDisplayName: node.data.name,
            price: node.data.priceValue,
            type: this.getAnalysisType(test.data.type)
          };
          items.push(testBundleTest);
        }
        break;
    }
    return items;
  }

  async getToolTipData(analysis: any): Promise<AnalysisToolTip> {
    let toolTipData: AnalysisToolTip;
    if (analysis.type === SalesCatalogueType.Test) {
      // get Test data
      const data = await this.analysesToolTipService.getTestCommercialItemToolTipData(analysis.code);
      toolTipData = data;
      toolTipData.testCode = data.code;
      toolTipData.localisedName = data.name;
      toolTipData.ciType = CiType.Test;
    } else if (analysis.type === SalesCatalogueType.Package) {
      // get Package data
      const data = await this.analysesToolTipService.getPackageCommercialItemToolTipData(analysis.code);
      toolTipData = data;
      toolTipData.packageCode = data.code;
      toolTipData.localisedName = data.name;
      toolTipData.ciType = CiType.Package;
    } else if (analysis.type === SalesCatalogueType.TestBundle) {
      // get Test Bundle data
      const data = await this.analysesToolTipService.getTestBundleCommercialItemToolTipData(analysis.code);
      toolTipData = data;
      toolTipData.packageCode = data.code;
      toolTipData.localisedName = data.name;
      toolTipData.ciType = CiType.TestBundle;
    }

    return toolTipData;
  }

  getAnalysisType(type) {
    switch (type) {
      case 0:
        return AnalysisType.SalesCatalogueTest;
      case 1:
        return AnalysisType.SalesCataloguePackage;
      case 2:
        return AnalysisType.SalesCatalogueTestBundle;
      default:
        return AnalysisType.SalesCatalogueTest;
    }
  }

  getCommercialItemType(type) {
    switch (type) {
      case 6:
        return MemberType.Test;
      case 7:
        return MemberType.Package;
      case 8:
        return MemberType.TestBundle;
      default:
        return MemberType.Test;
    }
  }

  getAlreadySelectedAnalysis(sample: Sample, alreadySelectedSCItems: any[], selectedSCItems: any[], unselectedSCItems: any[]): any[] {
    alreadySelectedSCItems = (sample.analyses?.filter(x => x.salesCatalogCode) ?? []);
    alreadySelectedSCItems = this.mapAnalysisArrayToCommercialItemArray(alreadySelectedSCItems);
    alreadySelectedSCItems = alreadySelectedSCItems.concat(selectedSCItems);
    alreadySelectedSCItems = alreadySelectedSCItems.filter(x => !unselectedSCItems.find(u =>
      u.testCode === x.testCode
      && u.salesCatalogCode === x.salesCatalogCode
      && u.testBundleCode === x.testBundleCode));
    return alreadySelectedSCItems;
  }

  async getQuotableItemCodesWithMatrix(): Promise<string[]> {
    let itemCodes = [];
    // Get Applicability from store
    const quotableItemApplicability = await this.storeService.Get<ProductApplicabilityResponseModel>
      (StoreKey.ProductApplicability).toPromise();
    // Get CommercialItem Applicabilities
    itemCodes = quotableItemApplicability.quotations[0]?.commercialItemApplicabilities?.
      filter(x => x.applicability === ApplicabilityEnum.Applicable)
      .map(x => x.commercialItemCode);
    // Combine with Subgroup Applicabilities
    quotableItemApplicability.quotations[0]?.subgroupApplicabilities?.
      filter(x => x.applicability === ApplicabilityEnum.Applicable)
      .forEach(x => itemCodes.push(x.membersApplicabilities
        .filter(m => m.applicability === ApplicabilityEnum.Applicable)
        .map(f => f.commercialItemCode))
      );

    const result = [].concat.apply([], itemCodes);
    return result;
  }

  getQuotableItemCodes(quotedItems: QuotedItem): string[] {
    let itemCodes = [];
    // Get test codes
    itemCodes = quotedItems?.quotationCommercialItem?.freeQuotationLinesTest?.filter(x => x.testCode != null).map(x => x.testCode);

    if (itemCodes && itemCodes.length > 0) {
      // Combine with package codes
      itemCodes = [...itemCodes,
      ...quotedItems?.quotationCommercialItem?.freeQuotationLinesPackage
        .filter(x => x.packageCode != null).map(x => x.packageCode)];
      // Combine with SG - test codes
      quotedItems?.quotationCommercialItem?.commercialItemSubGroups?.
        filter(c => itemCodes.push(c.testQuotationLines?.filter(t => t.testCode).map(t => t.testCode)));
      // Combine with SG - package codes
      quotedItems?.quotationCommercialItem?.commercialItemSubGroups?.
        filter(c => itemCodes.push(c.packageQuotationLines?.filter(t => t.packageCode).map(t => t.packageCode)));
    }
    const result = [].concat.apply([], itemCodes);

    return result;
  }
}
